/**
 * Выбор быстрого ответа в поле ввода.
 *
 * Текст вставляется в позицию курсора, а не заменяет набранное. Менеджер часто
 * пишет обращение сам («Игорь, добрый день!») и подставляет заготовку следом —
 * замена уничтожила бы уже набранное и была бы необратима, потому что отмены у
 * нас нет.
 *
 * Список грузится при первом открытии, а не вместе с чатом: заготовки нужны не
 * в каждом диалоге, а запрос на каждое переключение чата — лишняя нагрузка.
 */

import { useEffect, useRef, useState } from 'react';
import { type QuickReplyDto } from '../lib/api.js';
import { useSession } from '../state/session.tsx';
import { BoltIcon } from './icons.tsx';
import { RichText } from './RichText.tsx';
import { QuickReplyThumbs } from './QuickReplyThumbs.tsx';
import { Button } from '@/components/ui/button';

interface QuickReplyPickerProps {
  /**
   * Выбранная заготовка целиком, а не её текст.
   *
   * Раньше сюда уходила строка, и это было достаточно: заготовка была
   * текстом. Теперь у неё бывают вложения и премиальные эмодзи, у которых
   * текстового вида нет, — что делать с выбором, решает композер.
   */
  onPick: (reply: QuickReplyDto) => void;
  /** Чат, в котором открыт выбор: через него грузятся премиальные эмодзи. */
  chatId?: string;
  disabled?: boolean;
}

export function QuickReplyPicker({ onPick, chatId, disabled }: QuickReplyPickerProps) {
  const { api, can } = useSession();

  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<QuickReplyDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  const allowed = can('quickreply.view');

  useEffect(() => {
    if (!open || items !== null) return;
    api
      .listQuickReplies()
      .then(({ items: list }) => setItems(list))
      .catch(() => setError('Не удалось загрузить заготовки'));
  }, [open, items, api]);

  // Закрытие по клику вне и по Escape: всплывашка не должна оставаться висеть,
  // когда пользователь уже вернулся к набору текста.
  useEffect(() => {
    if (!open) return;

    const onDown = (e: MouseEvent): void => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!allowed) return null;

  return (
    <div className="relative flex-none" ref={boxRef}>
      <Button
        variant="ghost"
        className="size-[42px] flex-none rounded-full"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-expanded={open}
        aria-label="Быстрые ответы"
        title="Быстрые ответы"
      >
        <BoltIcon className="size-[18px]" />
      </Button>

      {/* Раскрывается вверх: поле ввода прижато к низу окна. */}
      {open && (
        <div
          role="listbox"
          className="absolute bottom-[calc(100%+8px)] left-0 z-20 max-h-80 w-[340px] overflow-y-auto rounded-2xl border border-border bg-popover p-1.5 shadow-2xl"
        >
          {error && <p className="p-3.5 text-[12.5px] leading-relaxed text-muted-foreground">{error}</p>}
          {!error && items === null && (
            <p className="p-3.5 text-[12.5px] text-muted-foreground">Загрузка…</p>
          )}
          {!error && items?.length === 0 && (
            <p className="p-3.5 text-[12.5px] leading-relaxed text-muted-foreground">
              Заготовок нет. Их создаёт старший менеджер в разделе «Управление».
            </p>
          )}

          {items?.map((item) => (
            <button
              key={item.id}
              type="button"
              role="option"
              className="flex w-full flex-col gap-0.5 rounded-xl px-2.5 py-2 text-left transition-colors hover:bg-accent"
              onClick={() => {
                onPick(item);
                setOpen(false);
              }}
            >
              <span className="text-[13px] font-semibold">{item.title}</span>
              {item.text && (
                <span className="line-clamp-2 text-xs text-muted-foreground">
                  <RichText text={item.text} entities={item.entities ?? []} chatId={chatId} />
                </span>
              )}
              {item.attachments && item.attachments.length > 0 && (
                <QuickReplyThumbs replyId={item.id} attachments={item.attachments} size="xs" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
